import * as Ariakit from '@ariakit/react/combobox'
import { CaretDownIcon, CaretUpIcon, CheckIcon, XIcon } from '@phosphor-icons/react'
import * as RadixPopover from '@radix-ui/react-popover'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import z from 'zod'
import { useIsScrolledToBottom } from '../../hooks/use-is-scrolled-to-bottom'
import { withBottomInnerShadowStyle } from '../../hooks/use-is-scrolled-to-bottom.css.ts'
import { Spinner } from '../spinner'
import * as styles from './combobox.css'

export const ComboboxSideSchema = z.enum(['top', 'bottom'])
export type ComboboxSide = z.infer<typeof ComboboxSideSchema>

/**
 * Hook that provides access to the combobox store state.
 * Throws an error if used outside of a ComboboxProvider.
 */
function useComboboxState() {
  const store = Ariakit.useComboboxContext()

  if (!store) {
    throw new Error('useComboboxState must be used within a ComboboxProvider')
  }

  return store
}

const MAX_OPTIONS_TO_SHOW = 10

type ComboboxSelectOption = {
  label: React.ReactNode
  value: string
  enabled?: boolean
  checked?: boolean
  // Rendered before the label in the list, and before the input when this option is the value.
  icon?: React.ReactNode
  // Optional second line under the label, for what distinguishes otherwise identical options.
  metadata?: string
}

export type ComboboxProps = {
  options: ComboboxSelectOption[]
  isFetching?: boolean
  /**
   * Whether the trigger offers a clear (✕) control once it holds a value. Clearing suits a free-text
   * search; a combobox standing in for a select has nothing meaningful to clear to, so those pass
   * `false` and leave the caret as the only control.
   */
  isClearable?: boolean
  /** Blocks opening the list and editing the value, for when there is nothing to choose between. */
  isDisabled?: boolean
  /** Puts the field in the error treatment, matching the other inputs' `data-error` contract. */
  isError?: boolean
  /** The input's text. Ariakit calls this `inputValue`; the prop keeps the React-idiomatic name. */
  value?: Ariakit.ComboboxProviderProps['inputValue']
  setValue?: Ariakit.ComboboxProviderProps['setInputValue']
} & Pick<Ariakit.ComboboxProviderProps, 'open' | 'setOpen'> &
  Pick<Ariakit.ComboboxProps, 'placeholder' | 'id'> &
  Pick<React.ComponentProps<typeof RadixPopover.Content>, 'side' | 'avoidCollisions'> & {
    'aria-label'?: string
    side?: ComboboxSide
  }

/**
 * Raw Combobox without search functionality. Before using this component, consider whether
 * @see SearchableCombobox is a better fit. This component was created mainly to separate
 * search functionality from other concerns, not because we have a need to use this component
 * directly.
 */
export function Combobox({
  options,
  placeholder,
  isFetching = false,
  isClearable = true,
  isDisabled = false,
  isError = false,
  value,
  setValue,
  open: controlledOpen,
  setOpen: controlledSetOpen,
  side,
  avoidCollisions,
  id,
  'aria-label': ariaLabel,
}: ComboboxProps) {
  const comboboxRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)

  const [uncontrolledOpen, uncontrolledSetOpen] = useState(false)
  const open = (controlledOpen ?? uncontrolledOpen) && !isDisabled
  const setOpen = controlledSetOpen ?? uncontrolledSetOpen

  return (
    <RadixPopover.Root open={open} onOpenChange={setOpen}>
      <Ariakit.ComboboxProvider open={open} setOpen={setOpen} setInputValue={setValue} inputValue={value}>
        <ComboboxContent
          comboboxRef={comboboxRef}
          listboxRef={listboxRef}
          options={options}
          isFetching={isFetching}
          side={side}
          avoidCollisions={avoidCollisions}
        />
        <ComboboxAnchor
          comboboxRef={comboboxRef}
          placeholder={placeholder}
          id={id}
          aria-label={ariaLabel}
          options={options}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isError={isError}
        />
      </Ariakit.ComboboxProvider>
    </RadixPopover.Root>
  )
}

// Right-hand room the input has to leave so a long value truncates instead of sliding under the
// controls: the container's own 24px inset, the 16px caret, and the 12px-gapped 12px clear icon.
const CARET_ONLY_INSET = '52px'
const CARET_AND_CLEAR_INSET = '76px'

const ComboboxAnchor = ({
  comboboxRef,
  placeholder,
  id,
  'aria-label': ariaLabel,
  options,
  isClearable,
  isDisabled,
  isError,
}: {
  comboboxRef: React.RefObject<HTMLInputElement | null>
  placeholder?: string
  id?: string
  'aria-label'?: string
  options: ComboboxSelectOption[]
  isClearable: boolean
  isDisabled: boolean
  isError: boolean
}) => {
  const store = useComboboxState()
  // The clear control belongs to typing, not to a settled value: it shows while the field has focus
  // and goes away once the user moves on, so a made selection is not left wearing a ✕.
  const [isFocused, setIsFocused] = useState(false)

  // Matched against `inputValue`, the typed text, because this wrapper never sets Ariakit's separate
  // `selectedValue`. So this cannot truly tell a selection from typing: free-typing a string equal to
  // an option's `value` also shows the icon. Accepted because consumers pass opaque ids as values.
  const selectedIcon = options.find(option => option.value === store.getState().inputValue)?.icon
  const hasSelectedIcon = Boolean(selectedIcon)
  const isClearControlAvailable = isClearable && !isDisabled
  const hasClearControl = isClearControlAvailable && isFocused && store.getState().inputValue !== ''

  return (
    <RadixPopover.Anchor asChild>
      <div
        className={styles.comboboxContainer}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}
        data-disabled={isDisabled ? 'true' : undefined}
        data-error={isError ? 'true' : undefined}
      >
        {hasSelectedIcon && (
          // Decorative: the option's identity is carried by its label, which is already read out.
          <span className={styles.iconAdornment} aria-hidden>
            {selectedIcon}
          </span>
        )}
        <Ariakit.Combobox
          ref={comboboxRef}
          placeholder={placeholder}
          className={classNames(styles.combobox, styles.comboboxTruncated, {
            [styles.comboboxWithIcon]: hasSelectedIcon,
          })}
          // The right inset depends on how many controls are rendered, so it stays a computed value;
          // the icon's left inset is a fixed pair with the glyph and lives in the stylesheet.
          style={{ paddingRight: isClearControlAvailable ? CARET_AND_CLEAR_INSET : CARET_ONLY_INSET }}
          id={id}
          aria-label={ariaLabel}
          disabled={isDisabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <div
          className={styles.comboboxControlsContainer}
          style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
        >
          {hasClearControl && (
            <Ariakit.ComboboxCancel
              className={styles.cancel}
              // Pressing the control would otherwise blur the input, which un-renders the control
              // before the click lands on it. Holding focus keeps it clickable.
              onMouseDown={event => event.preventDefault()}
              render={({ children: _, ...props }) => <XIcon size={16} weight="light" {...props} />}
            />
          )}
          {/* Kept, but inert, when disabled: SelectBox keeps its caret in that state, and the field
              reads as a select whether or not it currently has anything to open. */}
          <Ariakit.ComboboxDisclosure className={styles.disclosure} disabled={isDisabled}>
            <CaretDownIcon className={styles.caretDown} size={16} weight="fill" />
            <CaretUpIcon className={styles.caretUp} size={16} weight="fill" />
          </Ariakit.ComboboxDisclosure>
        </div>
      </div>
    </RadixPopover.Anchor>
  )
}

type RadixPopoverContentProps = React.ComponentProps<typeof RadixPopover.Content>
type InteractOutsideEvent = Parameters<Exclude<RadixPopoverContentProps['onInteractOutside'], undefined>>[0]

const ComboboxContent = ({
  comboboxRef,
  listboxRef,
  options,
  isFetching,
  side,
  avoidCollisions,
}: {
  comboboxRef: React.RefObject<HTMLInputElement | null>
  listboxRef: React.RefObject<HTMLDivElement | null>
  options: ComboboxSelectOption[]
  isFetching: boolean
} & Pick<React.ComponentProps<typeof RadixPopover.Content>, 'side' | 'avoidCollisions'>) => {
  const handleInteractOutside = useCallback(
    (event: InteractOutsideEvent) => {
      const target = event.target as Element | null
      const isCombobox = target === comboboxRef.current
      const inListbox = target && listboxRef.current?.contains(target)
      if (isCombobox || inListbox) {
        event.preventDefault()
      }
    },
    [comboboxRef.current, listboxRef.current]
  )

  const status = useMemo<ComboboxStatus>(() => {
    if (isFetching) {
      return 'loading'
    }

    if (options.length === 0) {
      return 'empty'
    }

    if (options.length > MAX_OPTIONS_TO_SHOW) {
      return 'partial'
    }

    return 'full'
  }, [isFetching, options.length])

  // Enforces the cap the 'partial' status promises. Without it the message said results were
  // withheld while the listbox rendered all of them, so at hundreds of instances of one tool a user
  // was told to keep typing and then handed the whole list.
  const visibleOptions = useMemo(() => options.slice(0, MAX_OPTIONS_TO_SHOW), [options])

  const store = useComboboxState()
  const isOpen = store.getState().open
  const listRef = useRef<HTMLDivElement | null>(null)
  const { isScrolledToBottom, handleScroll, handleChange } = useIsScrolledToBottom()

  useEffect(() => {
    if (listRef.current !== null) {
      // to set the bottom shadow when the listbox is opened our search list changes
      handleChange(listRef.current)
    }
  }, [visibleOptions.length, isOpen, handleChange, listRef.current])

  return (
    <RadixPopover.Content
      asChild
      side={side}
      onOpenAutoFocus={event => event.preventDefault()}
      onInteractOutside={handleInteractOutside}
      avoidCollisions={avoidCollisions}
      className={!isScrolledToBottom ? withBottomInnerShadowStyle : undefined}
    >
      <Ariakit.ComboboxList ref={listboxRef} className={styles.listbox}>
        <ComboboxInfoMessage status={status} />
        <div className={styles.listboxOptions} onScroll={handleScroll} ref={listRef}>
          {visibleOptions.map(({ label, value, enabled, checked, icon, metadata }) => (
            <ComboboxItem
              key={value}
              label={label}
              value={value}
              enabled={enabled}
              checked={checked}
              icon={icon}
              metadata={metadata}
            />
          ))}
        </div>
      </Ariakit.ComboboxList>
    </RadixPopover.Content>
  )
}

const ComboboxItem = ({ label, value, enabled = true, checked = false, icon, metadata }: ComboboxSelectOption) => {
  return (
    <Ariakit.ComboboxItem
      key={value}
      focusOnHover={enabled}
      disabled={!enabled}
      className={classNames(styles.option, { [styles.optionWithMetadata]: metadata !== undefined })}
      style={{ opacity: enabled ? 1 : 0.6 }}
      value={value}
    >
      <span
        className={classNames(styles.optionLabelWithIcon, {
          [styles.optionLabelTopAligned]: metadata !== undefined,
        })}
      >
        {/* Decorative, like the trigger's: the option's label already names it. */}
        {icon && <span aria-hidden>{icon}</span>}
        <span className={styles.optionLabel}>
          <span className={styles.optionText}>{label}</span>
          {metadata !== undefined && <span className={styles.optionMetadata}>{metadata}</span>}
        </span>
      </span>
      {checked && (
        <CheckIcon className={styles.checkIcon} size={20} weight="regular" role="img" aria-label="Selected" />
      )}
    </Ariakit.ComboboxItem>
  )
}

type ComboboxStatus = 'empty' | 'loading' | 'partial' | 'full'
const ComboboxInfoMessage = ({ status }: { status: ComboboxStatus }) => {
  const message = useMemo(
    () =>
      match(status)
        .with('empty', () => 'No results')
        .with('loading', () => 'Loading...')
        .with('partial', () => 'Keep typing for additional results')
        .with('full', () => null)
        .exhaustive(),
    [status]
  )

  if (status === 'full') {
    return null
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className={styles.infoMessageContainer}>
      {status === 'loading' && <Spinner size="16" />}
      <span className={styles.infoMessage}>{message}</span>
    </div>
  )
}
